import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  IoCheckmarkCircle,
  IoChevronDownOutline,
  IoCloseOutline,
  IoCopyOutline,
  IoDownloadOutline,
  IoExpandOutline,
  IoOpenOutline,
  IoShareOutline,
  IoTimeOutline
} from 'react-icons/io5';

import AtlasChartView from './charts/AtlasChartView';
import AtlasMapView from './maps/AtlasMapView';
import AtlasTableViz from './AtlasTableViz';
import AbsorptionWidget from '../absorption';

function safeText(value) {
  return typeof value === 'string' ? value : '';
}

function blockKey(block, idx) {
  return block?.block_id || `${block?.type || 'blk'}_${idx}`;
}

function sheetKey({ xlsxId, sheet, range }) {
  return `${xlsxId || ''}::${sheet || ''}::${range || ''}`;
}

// Regex to match image tokens in markdown: <<<image:img_xxx>>>
const IMAGE_TOKEN_RE = /<<<image:\s*(img_[a-f0-9]+)\s*>>>/gi;

// Component to render a single image with async loading
function BriefImage({ imageId, fetchImage, caption }) {
  const [imageUrl, setImageUrl] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!imageId || !fetchImage) {
      setError('Image unavailable');
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const url = await fetchImage(imageId);
        if (!cancelled) {
          setImageUrl(url);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || 'Failed to load image');
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [imageId, fetchImage]);

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (imageUrl && imageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [imageUrl]);

  if (loading) {
    return (
      <div className="atlas-brief-image atlas-brief-image-loading">
        <div className="atlas-brief-image-placeholder">Loading image...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="atlas-brief-image atlas-brief-image-error">
        <div className="atlas-brief-image-placeholder">{error}</div>
      </div>
    );
  }

  return (
    <div className="atlas-brief-image">
      <img src={imageUrl} alt={caption || 'Generated image'} />
      {caption ? <div className="atlas-brief-image-caption">{caption}</div> : null}
    </div>
  );
}

export default function AtlasBriefPanel({
  brief,
  mode = 'private', // 'private' | 'public'
  isOpen,
  isFullscreen,
  isPublishing,
  publishError,
  shareUrl,
  shareId, // For public image fetching
  versions = [], // Array of { version_id, version_number, published_at }
  currentVersionNumber = null, // Current version being viewed (null = live/draft)
  onClose,
  onToggleFullscreen,
  onPublish,
  onSelectVersion, // (versionId) => void - called when user selects a version
  fetchViz,
  fetchImage, // async (imageId) => blobUrl
  fetchXlsxRange,
  downloadXlsx
}) {
  const [vizCache, setVizCache] = useState(() => new Map());
  const [sheetCache, setSheetCache] = useState(() => new Map());
  const [copyState, setCopyState] = useState(null);
  const [versionMenuOpen, setVersionMenuOpen] = useState(false);
  const copyTimerRef = useRef(null);
  const versionMenuRef = useRef(null);

  const blocks = useMemo(() => (brief?.blocks && Array.isArray(brief.blocks) ? brief.blocks : []), [brief]);
  const embeddedViz = useMemo(() => brief?.assets?.viz || {}, [brief]);

  useEffect(() => {
    return () => {
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    };
  }, []);

  // Close version menu when clicking outside
  useEffect(() => {
    if (!versionMenuOpen) return undefined;
    const handleClickOutside = (e) => {
      if (versionMenuRef.current && !versionMenuRef.current.contains(e.target)) {
        setVersionMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [versionMenuOpen]);

  useEffect(() => {
    // Reset per-brief viz cache so stale names don't leak across briefs.
    setVizCache(new Map());
    setSheetCache(new Map());
    setVersionMenuOpen(false);
  }, [brief?.brief_id]);

  const ensureViz = useCallback(
    async (vizName) => {
      if (!vizName) return null;
      if (embeddedViz?.[vizName]) return embeddedViz[vizName];
      if (!fetchViz) return null;

      setVizCache((prev) => {
        if (prev.has(vizName)) return prev;
        const next = new Map(prev);
        next.set(vizName, { status: 'loading' });
        return next;
      });

      try {
        const payload = await fetchViz({ name: vizName });
        setVizCache((prev) => {
          const next = new Map(prev);
          next.set(vizName, { status: 'ready', payload });
          return next;
        });
        return payload;
      } catch (err) {
        setVizCache((prev) => {
          const next = new Map(prev);
          next.set(vizName, { status: 'error', error: err?.message || String(err) });
          return next;
        });
        return null;
      }
    },
    [embeddedViz, fetchViz]
  );

  useEffect(() => {
    if (!isOpen) return;
    if (!blocks.length) return;
    if (!fetchViz || mode !== 'private') return;

    // Best-effort prefetch viz payloads for smooth rendering.
    const names = [];
    for (const b of blocks) {
      if (!b || typeof b !== 'object') continue;
      const t = b.type;
      if (t !== 'viz' && t !== 'chart' && t !== 'map') continue;
      const n = b?.data?.viz_name || b?.data?.name;
      if (n) names.push(String(n));
    }
    const uniq = Array.from(new Set(names));
    if (!uniq.length) return;

    let cancelled = false;
    (async () => {
      for (const n of uniq) {
        if (cancelled) break;
        // eslint-disable-next-line no-await-in-loop
        await ensureViz(n);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [blocks, ensureViz, fetchViz, isOpen, mode]);

  const ensureSheetRange = useCallback(
    async ({ xlsxId, sheet, range }) => {
      if (!xlsxId || !sheet || !range) return null;
      if (!fetchXlsxRange) return null;

      const key = sheetKey({ xlsxId, sheet, range });
      setSheetCache((prev) => {
        if (prev.has(key)) return prev;
        const next = new Map(prev);
        next.set(key, { status: 'loading' });
        return next;
      });

      try {
        const payload = await fetchXlsxRange({ xlsxId, sheet, range });
        setSheetCache((prev) => {
          const next = new Map(prev);
          next.set(key, { status: 'ready', payload });
          return next;
        });
        return payload;
      } catch (err) {
        setSheetCache((prev) => {
          const next = new Map(prev);
          next.set(key, { status: 'error', error: err?.message || String(err) });
          return next;
        });
        return null;
      }
    },
    [fetchXlsxRange]
  );

  useEffect(() => {
    if (!isOpen) return;
    if (!blocks.length) return;
    if (!fetchXlsxRange || mode !== 'private') return;

    const sheetBlocks = [];
    for (const b of blocks) {
      if (!b || typeof b !== 'object') continue;
      if (b.type !== 'sheet_range') continue;
      const xlsxId = safeText(b?.data?.xlsx_id);
      const sheet = safeText(b?.data?.sheet);
      const range = safeText(b?.data?.range);
      if (xlsxId && sheet && range) sheetBlocks.push({ xlsxId, sheet, range });
    }
    const uniq = Array.from(new Map(sheetBlocks.map((s) => [sheetKey(s), s])).values());
    if (!uniq.length) return;

    let cancelled = false;
    (async () => {
      for (const s of uniq) {
        if (cancelled) break;
        // eslint-disable-next-line no-await-in-loop
        await ensureSheetRange(s);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [blocks, ensureSheetRange, fetchXlsxRange, isOpen, mode]);

  const handleCopyShare = useCallback(async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopyState('copied');
    } catch {
      setCopyState('error');
    }
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    copyTimerRef.current = setTimeout(() => setCopyState(null), 1200);
  }, [shareUrl]);

  const renderViz = useCallback(
    (vizName, vizTypeHint) => {
      if (!vizName) return null;
      const embedded = embeddedViz?.[vizName];
      const cached = vizCache.get(vizName);
      const payload = embedded || cached?.payload;

      if (!payload) {
        const status = cached?.status;
        if (status === 'error') {
          return <div className="atlas-brief-viz-error">Visualization failed to load.</div>;
        }
        return <div className="atlas-brief-viz-loading">Loading visualization…</div>;
      }

      const vizType = payload.type || vizTypeHint;
      if (vizType === 'absorption') {
        const widgetType = payload.widget_type || payload.widgetType || payload.data?.widget_type || 'single_prediction';
        const widgetData = payload.data || payload;
        return (
          <div className="atlas-brief-viz atlas-brief-viz-absorption">
            <AbsorptionWidget data={widgetData} widgetType={widgetType} />
          </div>
        );
      }
      // Legacy configs keep the brief's fixed-height wrappers; styled (meta) ones render as cards.
      const vizVariant = mode === 'private' ? 'brief' : 'shared';
      if (vizType === 'chart') {
        return <AtlasChartView config={payload.config} variant={vizVariant} />;
      }
      if (vizType === 'map') {
        return <AtlasMapView config={payload.config} variant={vizVariant} />;
      }
      if (vizType === 'table') {
        return <AtlasTableViz payload={payload} variant="brief" />;
      }
      return (
        <div className="atlas-brief-viz atlas-brief-viz-raw">
          <pre>{JSON.stringify(payload, null, 2)}</pre>
        </div>
      );
    },
    [embeddedViz, mode, vizCache]
  );

  const formatCell = useCallback((value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'number' && Number.isFinite(value)) {
      try {
        return value.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
      } catch {
        return String(value);
      }
    }
    return String(value);
  }, []);

  if (!isOpen) return null;

  return (
    <div className={['atlas-brief-panel', isFullscreen ? 'atlas-brief-panel-fullscreen' : ''].filter(Boolean).join(' ')}>
      <div className="atlas-brief-header">
        <div className="atlas-brief-header-left">
          {mode === 'public' ? (
            <a href="https://realtorch.ai" className="atlas-brief-brand-link" target="_blank" rel="noopener noreferrer">
              <span className="atlas-brief-brand-real">Real</span>
              <span className="atlas-brief-brand-torch">Torch</span>
            </a>
          ) : (
            <div className="atlas-brief-title-wrap">
              <span className="atlas-brief-label">Atlas Brief</span>
              {versions.length > 1 && (
                <div className="atlas-brief-version-wrap" ref={versionMenuRef}>
                  <button
                    type="button"
                    className="atlas-brief-version-btn"
                    onClick={() => setVersionMenuOpen((v) => !v)}
                    title="View all briefs in this conversation"
                  >
                    <span className="atlas-brief-version-label">
                      {versions.length} briefs
                    </span>
                    <IoChevronDownOutline size={12} />
                  </button>
                  {versionMenuOpen && (
                    <div className="atlas-brief-version-menu">
                      <div className="atlas-brief-version-menu-header">Briefs in Conversation</div>
                      {versions.map((v) => {
                        const isActive = v.brief_id === brief?.brief_id;
                        const isPublished = !!v.share_id;
                        const dateStr = v.created_at ? new Date(v.created_at).toLocaleDateString() : '';
                        // Smart truncation: preserve version suffix (v1, v2, etc.)
                        const rawTitle = v.displayTitle || v.title || 'Untitled Brief';
                        const versionMatch = rawTitle.match(/\s*\(v\d+\)$/);
                        const versionSuffix = versionMatch ? versionMatch[0] : '';
                        const titleWithoutVersion = versionMatch ? rawTitle.slice(0, -versionSuffix.length) : rawTitle;
                        const maxTitleLen = 40 - versionSuffix.length;
                        const displayTitle = titleWithoutVersion.length > maxTitleLen
                          ? titleWithoutVersion.substring(0, maxTitleLen - 3) + '...' + versionSuffix
                          : rawTitle;
                        return (
                          <button
                            key={v.version_id}
                            type="button"
                            className={`atlas-brief-version-item ${isActive ? 'atlas-brief-version-item-active' : ''}`}
                            title={rawTitle}
                            onClick={() => {
                              if (onSelectVersion) onSelectVersion(v.brief_id);
                              setVersionMenuOpen(false);
                            }}
                          >
                            <div className="atlas-brief-version-item-left">
                              <span className="atlas-brief-version-item-label">{displayTitle}</span>
                              {isPublished && (
                                <span className="atlas-brief-version-published-badge">
                                  <IoCheckmarkCircle size={12} />
                                  Published
                                </span>
                              )}
                            </div>
                            <span className="atlas-brief-version-item-date">
                              <IoTimeOutline size={12} />
                              {dateStr}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {mode === 'private' ? (
          <div className="atlas-brief-header-actions">
            <button className="atlas-brief-btn atlas-brief-btn-secondary" type="button" onClick={onPublish} disabled={!brief?.brief_id || isPublishing}>
              <IoShareOutline size={16} />
              {isPublishing ? 'Publishing…' : 'Share'}
            </button>
            {onToggleFullscreen && (
              <button className="atlas-brief-btn atlas-brief-btn-secondary" type="button" onClick={onToggleFullscreen} title={isFullscreen ? 'Show chat' : 'Fullscreen brief'}>
                {!isFullscreen && <IoExpandOutline size={16} />}
                {isFullscreen ? 'Show Chat' : 'Fullscreen Brief'}
              </button>
            )}
            <button className="atlas-brief-icon-btn" type="button" onClick={onClose} title="Close brief">
              <IoCloseOutline size={20} />
            </button>
          </div>
        ) : (
          <div className="atlas-brief-header-right-public">
            <div className="atlas-brief-title-stack">
              <span className="atlas-brief-label">Atlas Brief</span>
              <span className="atlas-brief-shared-label">Shared research packet</span>
            </div>
          </div>
        )}
      </div>

      {publishError ? <div className="atlas-brief-alert atlas-brief-alert-error">{publishError}</div> : null}
      {shareUrl ? (
        <div className="atlas-brief-share-row">
          <a className="atlas-brief-share-link" href={shareUrl} target="_blank" rel="noreferrer">
            <IoOpenOutline size={14} />
            <span>{shareUrl}</span>
          </a>
          <button className="atlas-brief-icon-btn" type="button" onClick={handleCopyShare} title="Copy link">
            <IoCopyOutline size={18} />
          </button>
          {copyState ? <span className="atlas-brief-share-status">{copyState === 'copied' ? 'Copied' : 'Copy failed'}</span> : null}
        </div>
      ) : null}

      <div className="atlas-brief-body">
        {!blocks.length ? (
          <div className="atlas-brief-empty">
            <div className="atlas-brief-empty-title">No content yet</div>
            <div className="atlas-brief-empty-subtitle">Atlas will add sections here when it packages results into a Brief.</div>
          </div>
        ) : (
          <>
            {/* Brief Title Banner */}
            {safeText(brief?.title) && (
              <div className="atlas-brief-title-banner">
                <h1 className="atlas-brief-title-banner-text">{safeText(brief?.title)}</h1>
                {safeText(brief?.kind) && (
                  <div className="atlas-brief-title-banner-subtitle">
                    {safeText(brief?.kind).replace(/_/g, ' ')}
                  </div>
                )}
              </div>
            )}
            {blocks.map((b, idx) => {
            const t = b?.type || 'markdown';
            if (t === 'markdown') {
              const markdownText = safeText(b?.data?.markdown);
              // Check if markdown contains image tokens
              const hasImageTokens = IMAGE_TOKEN_RE.test(markdownText);
              IMAGE_TOKEN_RE.lastIndex = 0; // Reset regex state

              if (hasImageTokens && fetchImage) {
                // Split markdown by image tokens and render interleaved
                const parts = [];
                let lastIndex = 0;
                let match;
                IMAGE_TOKEN_RE.lastIndex = 0;
                while ((match = IMAGE_TOKEN_RE.exec(markdownText)) !== null) {
                  // Add text before the token
                  if (match.index > lastIndex) {
                    parts.push({ type: 'text', content: markdownText.slice(lastIndex, match.index) });
                  }
                  // Add image token
                  parts.push({ type: 'image', imageId: match[1] });
                  lastIndex = match.index + match[0].length;
                }
                // Add remaining text
                if (lastIndex < markdownText.length) {
                  parts.push({ type: 'text', content: markdownText.slice(lastIndex) });
                }

                return (
                  <div key={blockKey(b, idx)} className="atlas-brief-block atlas-brief-block-markdown">
                    {parts.map((part, pIdx) => {
                      if (part.type === 'text') {
                        return (
                          <ReactMarkdown key={`md_${pIdx}`} remarkPlugins={[remarkGfm]}>
                            {part.content}
                          </ReactMarkdown>
                        );
                      }
                      return (
                        <BriefImage key={`img_${pIdx}`} imageId={part.imageId} fetchImage={fetchImage} />
                      );
                    })}
                  </div>
                );
              }

              // No image tokens, render as normal markdown
              return (
                <div key={blockKey(b, idx)} className="atlas-brief-block atlas-brief-block-markdown">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdownText}</ReactMarkdown>
                </div>
              );
            }
            if (t === 'viz' || t === 'chart' || t === 'map') {
              const name = safeText(b?.data?.viz_name || b?.data?.name);
              const caption = safeText(b?.data?.caption);
              const typeHint = safeText(b?.data?.viz_type) || (t === 'chart' ? 'chart' : t === 'map' ? 'map' : '');
              return (
                <div key={blockKey(b, idx)} className="atlas-brief-block atlas-brief-block-viz">
                  {mode === 'private' ? renderViz(name, typeHint) : embeddedViz?.[name] ? renderViz(name, typeHint) : <div className="atlas-brief-viz-loading">Visualization unavailable</div>}
                  {caption ? <div className="atlas-brief-viz-caption">{caption}</div> : null}
                </div>
              );
            }
            if (t === 'image') {
              const imageId = safeText(b?.data?.image_id);
              const caption = safeText(b?.data?.caption);
              return (
                <div key={blockKey(b, idx)} className="atlas-brief-block atlas-brief-block-image">
                  <BriefImage imageId={imageId} fetchImage={fetchImage} caption={caption} />
                </div>
              );
            }
            if (t === 'callout') {
              const tone = safeText(b?.data?.tone) || 'note';
              const text = safeText(b?.data?.text);
              return (
                <div key={blockKey(b, idx)} className={['atlas-brief-block', 'atlas-brief-block-callout', `atlas-brief-callout-${tone}`].join(' ')}>
                  {text}
                </div>
              );
            }
            if (t === 'sheet_range') {
              const xlsxId = safeText(b?.data?.xlsx_id);
              const sheet = safeText(b?.data?.sheet);
              const range = safeText(b?.data?.range);
              const title = safeText(b?.data?.title) || 'Spreadsheet';
              const filename = safeText(b?.data?.filename) || `${xlsxId || 'workbook'}.xlsx`;
              const key = sheetKey({ xlsxId, sheet, range });
              const cached = sheetCache.get(key);
              const payload = cached?.payload;
              const status = cached?.status;

              return (
                <div key={blockKey(b, idx)} className="atlas-brief-block atlas-brief-block-sheet">
                  <div className="atlas-brief-sheet">
                    <div className="atlas-brief-sheet-head">
                      <div className="atlas-brief-sheet-title">{title}</div>
                      {mode === 'private' && downloadXlsx && xlsxId ? (
                        <button
                          className="atlas-brief-sheet-download"
                          type="button"
                          onClick={() => downloadXlsx({ xlsxId, filename })}
                          title="Download .xlsx"
                        >
                          <IoDownloadOutline size={18} />
                        </button>
                      ) : null}
                    </div>

                    {mode !== 'private' ? (
                      <div className="atlas-brief-sheet-loading">Spreadsheet preview unavailable in shared view.</div>
                    ) : !payload ? (
                      <div className="atlas-brief-sheet-loading">
                        {status === 'error' ? 'Spreadsheet failed to load.' : 'Loading spreadsheet…'}
                      </div>
                    ) : (
                      <div className="atlas-brief-sheet-table-wrap">
                        <table className="atlas-brief-sheet-table">
                          <tbody>
                            {(payload?.values || []).map((row, rIdx) => (
                              // eslint-disable-next-line react/no-array-index-key
                              <tr key={`r_${rIdx}`}>
                                {(row || []).map((cell, cIdx) => {
                                  const isHeader = rIdx === 0;
                                  const isRowHeader = cIdx === 0;
                                  const cls = [
                                    'atlas-brief-sheet-cell',
                                    isHeader ? 'atlas-brief-sheet-cell-header' : '',
                                    isRowHeader ? 'atlas-brief-sheet-cell-rowhead' : ''
                                  ]
                                    .filter(Boolean)
                                    .join(' ');
                                  return (
                                    // eslint-disable-next-line react/no-array-index-key
                                    <td key={`c_${rIdx}_${cIdx}`} className={cls}>
                                      {formatCell(cell)}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <div className="atlas-brief-sheet-meta">
                          {safeText(sheet)} • {safeText(range)}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            }
            return (
              <div key={blockKey(b, idx)} className="atlas-brief-block atlas-brief-block-unknown">
                <pre>{JSON.stringify(b, null, 2)}</pre>
              </div>
            );
          })}
          </>
        )}
      </div>
    </div>
  );
}
